import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export type DateRange = '7d' | '30d' | '90d' | '1y' | 'all'
export type Period = 'daily' | 'weekly' | 'monthly'

export interface PatternsOverview {
  totalSessions: number
  averageRating: number | null
  totalDurationMinutes: number
  averageDurationMinutes: number | null
  avgPerDay: number
  avgPerWeek: number
  avgPerMonth: number
  trend: 'increasing' | 'decreasing' | 'stable'
  dateRange: DateRange
}

export interface TimelineDataPoint {
  period: string
  count: number
  avgRating: number | null
}

export interface TimelineData {
  timeline: TimelineDataPoint[]
  period: Period
  dateRange: DateRange
}

export interface PreferenceItem {
  name: string
  count: number
  percentage: number
}

export interface BrandPreference extends PreferenceItem {
  avgRating: number | null
}

export interface PreferencesData {
  brands: BrandPreference[]
  vitolas: PreferenceItem[]
  wrappers: PreferenceItem[]
  totalSessions: number
  dateRange: DateRange
}

export interface HourlyData {
  hour: number
  count: number
}

export interface DailyData {
  day: number
  name: string
  count: number
}

export interface SmokingTimesData {
  hourly: HourlyData[]
  daily: DailyData[]
  dateRange: DateRange
}

export interface RatingDistribution {
  rating: number | null
  count: number
}

export interface TopRatedCigar {
  name: string
  brandName: string
  avgRating: number
  count: number
}

export interface RatingCorrelationData {
  distribution: RatingDistribution[]
  topRated: TopRatedCigar[]
  dateRange: DateRange
}

export interface FrequencyPeriodData {
  sessions: number
  avgPerWeek: number
  avgPerDay: number
  dateRange: DateRange
}

export interface FrequencyChangeData {
  sessions: number
  avgPerWeek: number
  direction: 'increasing' | 'decreasing' | 'stable'
}

export interface TimeOfDayData {
  morning: number
  afternoon: number
  evening: number
  night: number
}

export interface FrequencyComparisonData {
  current: FrequencyPeriodData
  previous: FrequencyPeriodData
  change: FrequencyChangeData
  timeOfDay: TimeOfDayData
}

export interface FrequencyGoals {
  dailyGoal: number | null
  weeklyGoal: number | null
  monthlyGoal: number | null
}

export interface AnalyticsParams {
  dateRange?: DateRange
  period?: Period
}

export const analyticsApi = createApi({
  reducerPath: 'analyticsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/analytics',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Analytics'],
  endpoints: (builder) => ({
    getPatternsOverview: builder.query<PatternsOverview, AnalyticsParams>({
      query: (params) => ({ url: '/patterns', params }),
      transformResponse: (response: { success: boolean; data: PatternsOverview }) => response.data,
      providesTags: ['Analytics'],
    }),
    getTimeline: builder.query<TimelineData, AnalyticsParams>({
      query: (params) => ({ url: '/patterns/timeline', params }),
      transformResponse: (response: { success: boolean; data: TimelineData }) => response.data,
      providesTags: ['Analytics'],
    }),
    getPreferences: builder.query<PreferencesData, AnalyticsParams>({
      query: (params) => ({ url: '/patterns/preferences', params }),
      transformResponse: (response: { success: boolean; data: PreferencesData }) => response.data,
      providesTags: ['Analytics'],
    }),
    getSmokingTimes: builder.query<SmokingTimesData, AnalyticsParams>({
      query: (params) => ({ url: '/patterns/times', params }),
      transformResponse: (response: { success: boolean; data: SmokingTimesData }) => response.data,
      providesTags: ['Analytics'],
    }),
    getRatingCorrelation: builder.query<RatingCorrelationData, AnalyticsParams>({
      query: (params) => ({ url: '/patterns/ratings', params }),
      transformResponse: (response: { success: boolean; data: RatingCorrelationData }) =>
        response.data,
      providesTags: ['Analytics'],
    }),
    getFrequencyComparison: builder.query<FrequencyComparisonData, AnalyticsParams>({
      query: (params) => ({ url: '/patterns/frequency', params }),
      transformResponse: (response: { success: boolean; data: FrequencyComparisonData }) =>
        response.data,
      providesTags: ['Analytics'],
    }),
    getFrequencyGoals: builder.query<FrequencyGoals, void>({
      query: () => '/goals',
      transformResponse: (response: { success: boolean; data: FrequencyGoals }) => response.data,
      providesTags: ['Analytics'],
    }),
    updateFrequencyGoals: builder.mutation<FrequencyGoals, Partial<FrequencyGoals>>({
      query: (body) => ({ url: '/goals', method: 'PUT', body }),
      transformResponse: (response: { success: boolean; data: FrequencyGoals }) => response.data,
      invalidatesTags: ['Analytics'],
    }),
  }),
})

// ---- Preference Analytics Types ----

export interface PreferenceBreakdownItem {
  name: string
  count: number
  avgRating: number
  percentage: number
}

export interface PreferenceDashboardData {
  totalReviews: number
  hasEnoughData: boolean
  minimumRequired?: number
  dateRange: DateRange
  overallAvgRating?: number
  flavorAvgRating?: number | null
  drawAvgRating?: number | null
  burnAvgRating?: number | null
  byStrength?: PreferenceBreakdownItem[]
  byWrapper?: PreferenceBreakdownItem[]
  byOrigin?: PreferenceBreakdownItem[]
  byVitola?: PreferenceBreakdownItem[]
  byBrand?: PreferenceBreakdownItem[]
}

export interface PreferenceTimelinePoint {
  period: string
  reviewCount: number
  avgRating: number
  topStrength: string | null
  topWrapper: string | null
  topOrigin: string | null
}

export interface PreferenceTimelineData {
  totalReviews: number
  hasEnoughData: boolean
  minimumRequired?: number
  dateRange: DateRange
  timeline: PreferenceTimelinePoint[]
}

export interface PreferenceInsightsData {
  totalReviews: number
  hasEnoughData: boolean
  minimumRequired?: number
  overallAvgRating?: number
  insights: string[]
}

export interface RecommendationItem {
  cigarLineId: string
  name: string
  brandName: string
  strength: string | null
  wrapper: string | null
  origin: string | null
  score: number
  reasons: string[]
}

export interface RecommendationsData {
  totalReviews: number
  hasEnoughData: boolean
  minimumRequired?: number
  preferenceProfile?: {
    topStrength: string | null
    topWrapper: string | null
    topOrigin: string | null
  }
  recommendations: RecommendationItem[]
}

export type PreferenceAnalyticsParams = {
  dateRange?: DateRange
}

// ---- Extended endpoints ----

const extendedAnalyticsApi = analyticsApi.injectEndpoints({
  endpoints: (builder) => ({
    getPreferenceDashboard: builder.query<PreferenceDashboardData, PreferenceAnalyticsParams>({
      query: (params) => ({ url: '/preferences/dashboard', params }),
      transformResponse: (response: { success: boolean; data: PreferenceDashboardData }) =>
        response.data,
      providesTags: ['Analytics'],
    }),
    getPreferenceTimeline: builder.query<PreferenceTimelineData, PreferenceAnalyticsParams>({
      query: (params) => ({ url: '/preferences/timeline', params }),
      transformResponse: (response: { success: boolean; data: PreferenceTimelineData }) =>
        response.data,
      providesTags: ['Analytics'],
    }),
    getPreferenceInsights: builder.query<PreferenceInsightsData, PreferenceAnalyticsParams>({
      query: (params) => ({ url: '/preferences/insights', params }),
      transformResponse: (response: { success: boolean; data: PreferenceInsightsData }) =>
        response.data,
      providesTags: ['Analytics'],
    }),
    getRecommendations: builder.query<RecommendationsData, PreferenceAnalyticsParams>({
      query: (params) => ({ url: '/preferences/recommendations', params }),
      transformResponse: (response: { success: boolean; data: RecommendationsData }) =>
        response.data,
      providesTags: ['Analytics'],
    }),
  }),
})

export const {
  useGetPatternsOverviewQuery,
  useGetTimelineQuery,
  useGetPreferencesQuery,
  useGetSmokingTimesQuery,
  useGetRatingCorrelationQuery,
  useGetFrequencyComparisonQuery,
  useGetFrequencyGoalsQuery,
  useUpdateFrequencyGoalsMutation,
} = analyticsApi

export const {
  useGetPreferenceDashboardQuery,
  useGetPreferenceTimelineQuery,
  useGetPreferenceInsightsQuery,
  useGetRecommendationsQuery,
} = extendedAnalyticsApi
